import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Quotefield from '../../../../../../pages/dashboard/tradedocuments/quotation/quotefield/quotefield';
import {
    medthodsOfDispatch,
    typesOfShipment
} from '../../../../../../utils/appconstants';
import PortsAutocomplete from '../../../../../autocompletewithapi/PortsAutocomplete';
import Customdatepicker from '../../../../../customdatepicker/customdatepicker';
import CustomTextField from '../../../../../CustomTextInput';
import NewCustomSelect from '../../../../../newcustomselect/newcustomselect';
import { SuitUIButton } from '../../../../../SuitUIButton';
import UploadLogo from '../../../common/UploadLogo';
import UploadStamp from '../../../common/UploadStamp';
import { CorporatePartner } from '../../../../../../interfaces/CorporatePartner';
import {
    Quotation,
    QuotationFormErrors
} from '../../../../../../interfaces/Quotation';
import { TransformedItems } from '../../../../../../pages/dashboard/tradedocuments/tradeDocumentTypes/CommercialTypes';

interface QuotationStepOneFormProps {
    updateField: (name: string, value: string | number) => void;
    payload: Quotation;
    corporatePartners: CorporatePartner[];
    loadingCorporatepartners: boolean;
    setShowAddPartner: Dispatch<SetStateAction<boolean>>;
    errors: QuotationFormErrors;
    quoteFields: TransformedItems[];
    updateQuoteField: (
        index: number,
        attributeName: keyof TransformedItems,
        attributeValue: TransformedItems[keyof TransformedItems]
    ) => void;
    removeQuoteField: (index: number) => void;
    addQuoteField: () => void;
    activeStep: number;
    setActiveStep: (step: number) => void;
}

const StepOne = ({
    updateField,
    payload,
    corporatePartners,
    loadingCorporatepartners,
    setShowAddPartner,
    errors,
    quoteFields,
    updateQuoteField,
    removeQuoteField,
    addQuoteField
}: QuotationStepOneFormProps) => {
    return (
        <div className='w-full flex flex-col gap-4'>
            <UploadLogo {...{ payload, updateField }} />

            <UploadStamp {...{ payload, updateField }} />

            <NewCustomSelect
                selectValue={payload.sellerId as unknown as string}
                required
                selectOptions={
                    corporatePartners as unknown as (
                        | string
                        | Record<string, unknown>
                    )[]
                }
                name={'sellerId'}
                updateField={(_, value) => {
                    updateField('sellerId', value);
                    updateField(
                        'sellerAddress',
                        corporatePartners.filter(
                            (item: CorporatePartner) => item.id === value
                        )[0]?.address
                    );
                    updateField(
                        'sellerCountry',
                        corporatePartners.filter(
                            (item: CorporatePartner) => item.id === value
                        )[0]?.bankCountry
                    );
                    updateField(
                        'sellerName',
                        corporatePartners.filter(
                            (item: CorporatePartner) => item.id === value
                        )[0]?.name
                    );
                }}
                label={'Invoice From'}
                selectOptionLabel='name'
                loading={loadingCorporatepartners}
                dropdownRender={() => {
                    return (
                        <div className='px-4 border-t border-appcolorgreyfaint'>
                            <p
                                className='px-4 py-2 cursor-pointer text-appcolorprimary'
                                onClick={() => {
                                    setShowAddPartner(true);
                                }}
                            >
                                + Add a New Partner
                            </p>
                        </div>
                    );
                }}
            />

            <CustomTextField
                value={payload?.quoteNumber}
                error={errors?.quoteNumber.length > 0}
                helperText={
                    errors?.quoteNumber.length > 0
                        ? errors?.quoteNumber
                        : undefined
                }
                required
                id='quotation-quoteNumber'
                label='Quote Number'
                name='quoteNumber'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('quoteNumber', e.target.value);
                }}
            />

            <div>
                <Customdatepicker
                    value={payload?.issuedDate || null}
                    id='quotation-issuedDate'
                    label='Date'
                    name='issuedDate'
                    updateField={updateField}
                    error={errors?.issuedDate.length > 0}
                    helperText={
                        errors?.issuedDate.length > 0
                            ? errors?.issuedDate
                            : undefined
                    }
                />
            </div>

            <NewCustomSelect
                selectValue={payload.receiverId as unknown as string}
                required
                selectOptions={
                    corporatePartners as unknown as (
                        | string
                        | Record<string, unknown>
                    )[]
                }
                name={'receiverId'}
                updateField={(_, value) => {
                    updateField('receiverId', value);
                    updateField(
                        'receiverAddress',
                        corporatePartners.filter(
                            (item: CorporatePartner) => item.id === value
                        )[0]?.address
                    );
                    updateField(
                        'receiverCountry',
                        corporatePartners.filter(
                            (item: CorporatePartner) => item.id === value
                        )[0]?.bankCountry
                    );
                    updateField(
                        'receiverName',
                        corporatePartners.filter(
                            (item: CorporatePartner) => item.id === value
                        )[0]?.name
                    );
                }}
                label={'Invoice  To'}
                selectOptionLabel='name'
                loading={loadingCorporatepartners}
                dropdownRender={() => {
                    return (
                        <div className='px-4 border-t border-appcolorgreyfaint'>
                            <p
                                className='px-4 py-2 cursor-pointer text-appcolorprimary'
                                onClick={() => {
                                    setShowAddPartner(true);
                                }}
                            >
                                + Add a New Partner
                            </p>
                        </div>
                    );
                }}
            />

            <PortsAutocomplete
                errorCondition={errors?.portOfLoadingId.length > 0}
                helperText={
                    errors?.portOfLoadingId.length > 0
                        ? errors?.portOfLoadingId
                        : undefined
                }
                id='quotation-portOfLoading'
                label='Port of Loading'
                name='portOfLoadingId'
                updateField={updateField}
                nameValue='portOfLoadingValue'
                isRequired={true}
                value={payload.portOfLoadingValue}
            />

            <CustomTextField
                value={payload?.methodOfDispatch}
                select
                error={errors?.methodOfDispatch.length > 0}
                helperText={
                    errors?.methodOfDispatch.length > 0
                        ? errors?.methodOfDispatch
                        : undefined
                }
                required
                id='quotation-methodOfDispatch'
                label='Method of Dispatch'
                name='methodOfDispatch'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('methodOfDispatch', e.target.value);
                }}
            >
                {medthodsOfDispatch.map((item, index) => (
                    <MenuItem
                        value={item}
                        key={`medthodsOfDispatch-${item}-${index}`}
                    >
                        {item}
                    </MenuItem>
                ))}
            </CustomTextField>

            <CustomTextField
                value={payload?.typeOfShipment}
                select
                error={errors?.typeOfShipment.length > 0}
                helperText={
                    errors?.typeOfShipment.length > 0
                        ? errors?.typeOfShipment
                        : undefined
                }
                required
                id='quotation-typeOfShipment'
                label='Type of Shipment'
                name='typeOfShipment'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('typeOfShipment', e.target.value);
                }}
            >
                {typesOfShipment.map((item, index) => (
                    <MenuItem
                        value={item}
                        key={`typesOfShipment-${item}-${index}`}
                    >
                        {item}
                    </MenuItem>
                ))}
            </CustomTextField>

            <div className='border border-gray300 border-dashed mt-4' />
            <CustomTextField
                error={errors?.salesContractNumber?.length > 0}
                helperText={
                    errors?.salesContractNumber?.length > 0
                        ? errors?.salesContractNumber
                        : undefined
                }
                value={payload?.salesContractNumber}
                id='quotation-salesContractNumber'
                label='Sales Contract Number'
                name='salesContractNumber'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('salesContractNumber', e.target.value);
                }}
            />

            {quoteFields.map((item, index) => (
                <Quotefield
                    item={item}
                    key={item.id}
                    index={index}
                    id={item?.id ?? ''}
                    updateQuoteField={updateQuoteField}
                    price={item.price ?? 0}
                    qty={item.qty ?? 0}
                    removeQuoteField={removeQuoteField}
                />
            ))}

            <SuitUIButton
                text='Add field'
                onClick={addQuoteField}
                invert
                fullWidth
                style='px-[0.92rem] border border-gray300 text-steelBlue font-medium text-sm leading-[20px] lg:border-none lg:text-appcolorprimary'
            />
        </div>
    );
};

export default StepOne;
